import { Link } from 'react-router-dom';
import { luxurySalesPricingPlans } from '@/mocks/corporateLuxurySalesData';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/lib/formatPrice';

export default function PricingTable() {
  const { t } = useTranslation();
  return (
    <section className="relative py-20 md:py-28 bg-background-100/50" id="pricing">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            PRICING
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('corporateLuxurySales.pricing.title')}
          </h2>
          {String(t('corporateLuxurySales.pricing.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto mb-5"></div>
          {String(t('corporateLuxurySales.pricing.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto">{__s}</p>))}
        </div>

        <div className="overflow-hidden mb-12">
          <div className="hidden md:grid grid-cols-4 bg-primary-900 text-foreground-50 text-[14px] tracking-[0.06em] font-label">
            <div className="p-4 text-center border-r border-white/10">{t('corporateLuxurySales.pricing.headerPlan')}</div>
            <div className="p-4 text-center border-r border-white/10">{t('corporateLuxurySales.pricing.headerTime')}</div>
            <div className="p-4 text-center border-r border-white/10">{t('corporateLuxurySales.pricing.headerPrice')}</div>
            <div className="p-4 text-center">{t('corporateLuxurySales.pricing.headerNote')}</div>
          </div>

          {luxurySalesPricingPlans.map((plan, idx) => (
            <div
              key={idx}
              className={`grid grid-cols-1 md:grid-cols-4 border-b ${
                plan.recommended
                  ? 'bg-accent-500/5 border-l-2 border-r-2 border-accent-500/40'
                  : 'bg-background-50 border-background-200/60'
              }`}
            >
              <div className="md:hidden p-4 flex flex-wrap items-center gap-2">
                <span className="text-[15px] text-foreground-900 font-medium tracking-[0.05em]">{t(`corporateLuxurySales.pricing.p${idx + 1}.name`)}</span>
                {plan.recommended && (
                  <span className="text-[12px] bg-accent-500/80 text-primary-900 px-2 py-0.5 tracking-[0.08em] font-bold">{t('corporateLuxurySales.pricing.standard')}</span>
                )}
                <span className="text-[14px] text-foreground-500 ml-auto">{t(`corporateLuxurySales.pricing.p${idx + 1}.duration`)}</span>
              </div>
              <div className="md:hidden px-4 pb-3 flex items-center justify-between">
                <span className="text-[17px] text-accent-600 font-medium tracking-[0.04em]">{formatPrice(plan.price)}{t('corporateLuxurySales.pricing.currency')}</span>
                <span className="text-[13px] text-foreground-400">{t(`corporateLuxurySales.pricing.p${idx + 1}.note`)}</span>
              </div>

              <div className="hidden md:flex items-center p-4 border-r border-background-200/60">
                <span className="text-[15px] text-foreground-900 font-medium tracking-[0.05em]">{t(`corporateLuxurySales.pricing.p${idx + 1}.name`)}</span>
              </div>
              <div className="hidden md:flex items-center p-4 border-r border-background-200/60">
                <span className="text-[14px] text-foreground-600 tracking-[0.04em]">{t(`corporateLuxurySales.pricing.p${idx + 1}.duration`)}</span>
              </div>
              <div className="hidden md:flex items-center p-4 border-r border-background-200/60">
                <span className="text-[17px] text-accent-600 font-medium tracking-[0.04em]">{formatPrice(plan.price)}{t('corporateLuxurySales.pricing.currency')}</span>
              </div>
              <div className="hidden md:flex items-center justify-center p-4">
                {plan.recommended ? (
                  <span className="text-[12px] bg-accent-500/80 text-primary-900 px-2.5 py-1 tracking-[0.08em] font-bold">{t('corporateLuxurySales.pricing.standard')}</span>
                ) : (
                  <span className="text-[14px] text-foreground-500 tracking-[0.04em]">{t(`corporateLuxurySales.pricing.p${idx + 1}.note`)}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-[650px] mx-auto bg-background-50 border border-background-200/60 p-6 mb-10">
          <h3 className="font-heading text-[15px] text-foreground-800 tracking-[0.06em] mb-4">{t('corporateLuxurySales.pricing.conditionsTitle')}</h3>
          <ul className="space-y-2.5">
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent-500 text-xs"></i>
              </div>
              {t('corporateLuxurySales.pricing.conditions.dispatch')}
            </li>
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent-500 text-xs"></i>
              </div>
              {t('corporateLuxurySales.pricing.conditions.capacity')}
            </li>
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent-500 text-xs"></i>
              </div>
              {t('corporateLuxurySales.pricing.conditions.payment')}
            </li>
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent-500 text-xs"></i>
              </div>
              {t('corporateLuxurySales.pricing.conditions.nightAvailable')}
            </li>
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-400 tracking-[0.04em] leading-relaxed pt-2 border-t border-background-200/60">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-foreground-400 text-xs"></i>
              </div>
              {t('corporateLuxurySales.pricing.conditions.cancel')}
            </li>
          </ul>
        </div>

        <p className="text-center mb-10 text-[14px] text-foreground-400 tracking-[0.04em]">
          {t('corporateLuxurySales.pricing.allMenuNote')}{' '}
          <Link to="/corporate" className="text-accent-600 hover:text-accent-500 transition-colors">
            {t('corporateLuxurySales.pricing.allMenuLink')}
          </Link>
          {t('corporateLuxurySales.pricing.allMenuSuffix')}
        </p>

        <div className="text-center">
          <a
            href="#contact-form"
            className="btn-glow inline-block px-10 py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('corporateLuxurySales.pricing.ctaBtn')}
          </a>
        </div>
      </div>
    </section>
  );
}
